import Vue from 'vue'
import VueRouter from 'vue-router'
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta'
import routes from './routes'
import store from '../state/store'
import axios from "axios";
import { apiStatus } from '@utils/apiStatus';
import { shouldReloadForNewBuild } from '@utils/appVersion';

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

const router = new VueRouter({
  routes,
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
});

// Target of the in-flight navigation — see the EXPIRED redirect and the chunk-failure handler below.
let lastNavigationTarget = null;

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  lastNavigationTarget = routeTo;

  // Leave guards have already passed. If a beforeunload prompt keeps the user
  // here, they stay on the current route and the next page change retries.
  if (shouldReloadForNewBuild(routeTo, routeFrom)) {
    window.location.assign(routeTo.fullPath);
    return next(false);
  }

  // Check if auth is required on this route
  // (including nested routes).
  const authRequired = routeTo.matched.some((route) => route.meta.authRequired);
  const adminRequired = routeTo.matched.some((route) => route.meta.adminRequired);

  // If auth isn't required for the route, just continue.
  if (!authRequired) return next();

  // If auth is required and the user is NOT currently logged in,
  // redirect to login.
  if (!store.getters['auth/loggedIn']) return redirectToLogin();

  // Validate the local session, and for admin routes hold the navigation
  // until the server confirms admin status — the route must not render
  // on a stale or non-admin session.
  return store.dispatch('auth/validate').then((validUser) => {
    if (!validUser) return redirectToLogin();
    if (!adminRequired) return next();

    return store.dispatch('auth/validateAdmin')
      .then(() => next())
      .catch(() => next({ path: '/' }));
  });

  function redirectToLogin() {

    if(routeTo.query.redirect_from_google_calendar){
      return next({name: 'google-calendar-redirect'});
    }
    // Pass the original route to the login component
    next({ name: 'login', query: { redirectFrom: routeTo.fullPath } })
  }
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
});


const PASSCODE_CODES = ['PASSCODE_REQUIRED', 'PASSCODE_TOKEN_EXPIRED', 'PASSCODE_INVALID_TOKEN'];
// where an expired account is sent, and where it pays
const SUBSCRIPTION_ROUTES = ['user-subscription', 'pay-transaction'];

export const axiosSuccessInterceptor = (res) => {
  apiStatus.reportResponse(res);
  return res;
}

// Redirects and logouts are side effects: the request still rejects with its own error.
export const axiosFailInterceptor = (res) => {
  apiStatus.reportError(res);

  if (res.response?.data?.code === 'ACCESS_PAUSED') {
    router.push({name: 'auth-paused'});
    return Promise.reject(res);
  }

  // Passcode-protected endpoint — clear the stale token so the gate re-locks
  if (res.response && res.response.status === 403 && PASSCODE_CODES.includes(res.response.data?.code)) {
    store.dispatch('auth/clearPasscodeToken');
    return Promise.reject(res);
  }

  if(res.response && res.response.status === 401 && res.response.data.code === "NO_TOKEN" &&
      !res.response.config.url.includes('mobile.athlisis.com')){
    store.dispatch('auth/logOut').then(()=> {

      // // if a customer clicks on google calendar, he receives 401 and is lead here!
      // if(router.currentRoute.query.redirect_from_google_calendar){
      //   return router.push({name: 'google-calendar-redirect'})
      // }
      // check if current route requires Auth. If yes, redirect to login!
      if(router.currentRoute.meta.authRequired) {
        return router.push({name: 'login'});
      }
    });
  }

  if(res.response && res.response.data.code === "NOT_PURCHASED"){
    store.dispatch('auth/notPurchasedState');
    router.push({name:'purchase-athlisis'});
    return Promise.reject(res);
  }
  if(res.response && res.response.data && res.response.data.code === "EXPIRED"){
    // a push would cancel a navigation already headed there, like Stripe's return to the payment page
    if (!SUBSCRIPTION_ROUTES.includes(lastNavigationTarget?.name)) router.push({name: 'user-subscription'});
    return Promise.reject(res);
  }

  if(res.response && res.response.status === 401 && res.response.data.code === "TOKEN_EXPIRED"){
    store.dispatch('auth/logOut');
    return Promise.reject(res);
  }

  // Quota hit — surface the shared modal, but still reject so callers can
  // stop their own spinners / form states.
  if (res.response?.data?.code === 'quota_exceeded') {
    store.dispatch('quota/show', res.response.data);
  }

  return Promise.reject(res);
}

// intercept on token issues!
axios.interceptors.response.use(
    (res) => axiosSuccessInterceptor(res),
    (res) => axiosFailInterceptor(res)
);

// After a deploy the previous build's chunk files are gone, so a client on
// the old index.html 404s the first time it visits a section it hasn't
// fetched yet. One hard reload onto the intended route picks up the fresh
// build; the flag (cleared on any successful navigation) keeps a genuine
// outage from reload-looping.
const CHUNK_RELOAD_FLAG = 'athlisis-chunk-reload';

router.onError((error) => {
  const chunkFailed =
    error?.name === 'ChunkLoadError' ||
    /loading (css )?chunk .+ failed/i.test(error?.message || '');
  if (!chunkFailed) return;

  if (!sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
    window.location.assign(lastNavigationTarget?.fullPath || window.location.pathname);
    return;
  }

  // Still failing on a fresh copy of the build means the deployed dist
  // itself is broken — nothing sensible to do automatically.
  console.error('Chunk failed to load after a fresh reload:', error);
});

import { trackPageView } from '@utils/analytics';

router.afterEach((to) => {
  sessionStorage.removeItem(CHUNK_RELOAD_FLAG);

  const cleanPath = to.matched.length
    ? to.matched[to.matched.length - 1].path
    : to.fullPath;
  trackPageView(cleanPath + (to.hash || ''));
});

export default router
